import { TextField, useFormContext, useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconCalendarRepeat } from '@calendly/ui/components/glyphs/IconCalendarRepeat'
import { IconEdit } from '@calendly/ui/components/glyphs/IconEdit'
import { IconButton } from '@calendly/ui/components/icon-button'
import { SwitchInput } from '@calendly/ui/components/switch-input'
import { BodyText, LabelText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { colorIconAction, colorIconSecondary, colorIconStandard } from '@calendly/ui/theme'
import { useCallback } from 'react'

import {
  CardActions,
  CardContent,
  CardIconWrapper,
  CollapsedCard,
  ExpandedCard,
  ExpandedCardBody,
  ExpandedCardFooter,
  ExpandedCardHeader,
  SwitchToggleLabel,
} from './styles'

export const SCHEDULE_ANOTHER_MEETING_EDITING_KEY = 'schedule-another-meeting'

const defaultScheduleAnotherLabel = () => I18n.t('publisher.event_type_editor_crud.confirmation.schedule_another_meeting_title')

export const ScheduleAnotherMeetingCard = ({
  disabled = false,
  editingKey,
  onCancel,
  onDone,
  onEdit,
}) => {
  const { setFieldTouched } = useFormContext()
  const [{ value: enabled }, _, { setValue: setEnabled }] = useFormField({ name: 'anotherEventButtonEnabled' })
  const [{ value: label }] = useFormField({ name: 'anotherEventButtonLabel' })
  const isEditing = editingKey === SCHEDULE_ANOTHER_MEETING_EDITING_KEY
  const displayLabel = label || defaultScheduleAnotherLabel()

  const handleDone = useCallback(() => {
    setFieldTouched('anotherEventButtonLabel', true)
    onDone()
  }, [onDone, setFieldTouched])

  if (isEditing) {
    return (
      <ExpandedCard data-testid="schedule-another-meeting-card-editing">
        <ExpandedCardHeader>
          <CardIconWrapper>
            <UiIcon color={colorIconAction} icon={IconCalendarRepeat} size="medium" title="" />
          </CardIconWrapper>
          <CardContent>
            <LabelText>{defaultScheduleAnotherLabel()}</LabelText>
            <BodyText color="muted" size="small">
              {I18n.t('publisher.event_type_editor_crud.confirmation.schedule_another_meeting_description')}
            </BodyText>
          </CardContent>
        </ExpandedCardHeader>
        <ExpandedCardBody>
          <TextField
            autoFocus
            aria-label={I18n.t('publisher.event_type_editor_crud.confirmation.link_text_label')}
            label={I18n.t('publisher.event_type_editor_crud.confirmation.link_text_label')}
            name="anotherEventButtonLabel"
            placeholder={defaultScheduleAnotherLabel()}
            required
          />
        </ExpandedCardBody>
        <ExpandedCardFooter>
          <Button appearance="ghost" size="small" type="button" onClick={onCancel}>
            {I18n.t('publisher.action.cancel')}
          </Button>
          <Button appearance="primary" size="small" type="button" onClick={handleDone}>
            {I18n.t('publisher.action.done')}
          </Button>
        </ExpandedCardFooter>
      </ExpandedCard>
    )
  }

  return (
    <CollapsedCard data-testid="schedule-another-meeting-card">
      <CardIconWrapper>
        <UiIcon
          color={enabled ? colorIconAction : colorIconSecondary}
          icon={IconCalendarRepeat}
          size="medium"
          title=""
        />
      </CardIconWrapper>
      <CardContent>
        <LabelText color={label ? 'standard' : 'muted'}>{displayLabel}</LabelText>
        <BodyText color="muted" size="small">
          {I18n.t('publisher.event_type_editor_crud.confirmation.schedule_another_meeting_description')}
        </BodyText>
      </CardContent>
      <CardActions>
        <IconButton
          aria-label={I18n.t('publisher.event_type_editor_crud.confirmation.customize')}
          disabled={disabled}
          size="small"
          title={I18n.t('publisher.event_type_editor_crud.confirmation.customize')}
          onClick={onEdit}
        >
          <UiIcon color={colorIconStandard} icon={IconEdit} size="small" title="" />
        </IconButton>
        <SwitchToggleLabel>
          <SwitchInput
            aria-label={defaultScheduleAnotherLabel()}
            checked={enabled}
            disabled={disabled}
            name="anotherEventButtonEnabled"
            onChange={() => setEnabled(!enabled)}
          />
        </SwitchToggleLabel>
      </CardActions>
    </CollapsedCard>
  )
}
